// Date-only + combined date/time helpers for the custom DatePicker/DateTimePicker
// segmented mask inputs, plus the Calendar drilldown navigation grid. Component files
// should stay focused on state/JSX and delegate pure date/time logic here.

import type { SegmentDef } from './segmented-input-utils';
import { padDigits } from './segmented-input-utils';
import {
  displayHour,
  getPeriod,
  parseTime,
  type TimePrecision,
} from '@/components/ui/shadcn/custom/TimePicker/time-utils';
import type { Locale } from 'react-day-picker';

// ---- Date-only segments (month/day/year), used by DatePicker and the date
// portion of DateTimePicker. ----

export type DateSegmentKey = 'month' | 'day' | 'year';
export type DateSegmentState = Record<DateSegmentKey, string>;

export const EMPTY_DATE_SEGMENTS: DateSegmentState = {
  month: '',
  day: '',
  year: '',
};

export const DATE_SEGMENT_DEFS: Record<DateSegmentKey, SegmentDef> = {
  month: { length: 2, min: 1, max: 12 },
  day: { length: 2, min: 1, max: 31 },
  year: { length: 4, min: 0, max: 9999 },
};

export function segmentsFromDate(date: Date | undefined): DateSegmentState {
  if (!date) return { ...EMPTY_DATE_SEGMENTS };
  return {
    month: padDigits(date.getMonth() + 1, 2),
    day: padDigits(date.getDate(), 2),
    year: padDigits(date.getFullYear(), 4),
  };
}

// Builds a Date from complete month/day/year segments and flags calendar-invalid
// combinations (e.g. Feb 30) that JS's Date would otherwise silently roll over.
export function buildDateFromSegments(
  segments: DateSegmentState
): { date: Date; isRealDate: boolean } | undefined {
  if (
    segments.month.length !== 2 ||
    segments.day.length !== 2 ||
    segments.year.length !== 4
  ) {
    return undefined;
  }

  const year = Number(segments.year);
  const month = Number(segments.month);
  const day = Number(segments.day);
  const candidate = new Date(year, month - 1, day);
  const isRealDate =
    candidate.getFullYear() === year &&
    candidate.getMonth() === month - 1 &&
    candidate.getDate() === day;

  return { date: candidate, isRealDate };
}

// ---- Time formatting/parsing, used by DateTimePicker and TimePicker fields. ----

export function formatTimeFromDate(
  date: Date,
  use12Hour: boolean,
  precision: TimePrecision
) {
  const hour = padDigits(displayHour(date.getHours(), use12Hour), 2);
  const minute = padDigits(date.getMinutes(), 2);
  const second = padDigits(date.getSeconds(), 2);

  const base =
    precision === 'hour'
      ? `${hour}:00`
      : precision === 'minute'
        ? `${hour}:${minute}`
        : `${hour}:${minute}:${second}`;

  return use12Hour ? `${base} ${getPeriod(date.getHours())}` : base;
}

export function combineDateAndTime(
  date: Date | undefined,
  time: string | undefined
) {
  if (!date) return undefined;

  const parsedTime = parseTime(time);
  const dateTime = new Date(date);

  if (!parsedTime) {
    dateTime.setHours(0, 0, 0, 0);
    return dateTime;
  }

  dateTime.setHours(parsedTime.hour24, parsedTime.minute, parsedTime.second, 0);
  return dateTime;
}

// ---- Combined date+time segments (month/day/year/hour/minute/second/period),
// used by DateTimePicker. ----

export type DateTimeSegmentKey =
  | DateSegmentKey
  | 'hour'
  | 'minute'
  | 'second'
  | 'period';
export type DateTimeSegmentState = Record<DateTimeSegmentKey, string>;

export const EMPTY_DATE_TIME_SEGMENTS: DateTimeSegmentState = {
  month: '',
  day: '',
  year: '',
  hour: '',
  minute: '',
  second: '',
  period: '',
};

export function buildDateTimeSegmentDefs(
  use12Hour: boolean
): Record<DateTimeSegmentKey, SegmentDef> {
  return {
    month: DATE_SEGMENT_DEFS.month,
    day: DATE_SEGMENT_DEFS.day,
    year: DATE_SEGMENT_DEFS.year,
    hour: { length: 2, min: use12Hour ? 1 : 0, max: use12Hour ? 12 : 23 },
    minute: { length: 2, min: 0, max: 59 },
    second: { length: 2, min: 0, max: 59 },
    period: { length: 2, min: 0, max: 0 },
  };
}

export function buildTimeString(
  segments: DateTimeSegmentState,
  use12Hour: boolean,
  precision: TimePrecision
) {
  const base =
    precision === 'second'
      ? `${segments.hour}:${segments.minute}:${segments.second}`
      : `${segments.hour}:${segments.minute}`;

  return use12Hour ? `${base} ${segments.period}` : base;
}

export function segmentsFromTime(
  time: string,
  use12Hour: boolean,
  precision: TimePrecision
): Pick<DateTimeSegmentState, 'hour' | 'minute' | 'second' | 'period'> {
  const parsedTime = parseTime(time);
  if (!parsedTime) return { hour: '', minute: '', second: '', period: '' };

  return {
    hour: padDigits(displayHour(parsedTime.hour24, use12Hour), 2),
    minute: padDigits(parsedTime.minute, 2),
    second: precision === 'second' ? padDigits(parsedTime.second, 2) : '',
    period: use12Hour ? getPeriod(parsedTime.hour24) : '',
  };
}

// ---- Calendar drilldown navigation (month/year grid picker), used by the
// 'drilldown' navigationVariant in ui/calendar.tsx via useCalendarDrilldown. ----

export type CalendarDrilldownView = 'days' | 'months' | 'years';

export type CalendarOptionGridItem = {
  key: number;
  label: string;
  isSelected: boolean;
  isCurrent: boolean;
};

export function buildMonthGridItems(
  year: number,
  selectedDate: Date | undefined,
  today: Date,
  locale: Partial<Locale> | undefined
): CalendarOptionGridItem[] {
  return Array.from({ length: 12 }, (_, month) => {
    const isSelected =
      selectedDate?.getFullYear() === year &&
      selectedDate?.getMonth() === month;
    const isCurrent =
      !isSelected && today.getFullYear() === year && today.getMonth() === month;
    return {
      key: month,
      label: new Date(year, month, 1).toLocaleString(locale?.code, {
        month: 'short',
      }),
      isSelected,
      isCurrent,
    };
  });
}

export function buildYearGridItems(
  decadeStart: number,
  selectedDate: Date | undefined,
  today: Date
): CalendarOptionGridItem[] {
  return Array.from({ length: 12 }, (_, i) => decadeStart + i).map((year) => {
    const isSelected = selectedDate?.getFullYear() === year;
    const isCurrent = !isSelected && today.getFullYear() === year;
    return { key: year, label: String(year), isSelected, isCurrent };
  });
}
